import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useMemo, useRef } from "react";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { aggridHeaderClass } from "src/components/common/common";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { defaultColDef, getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";
import { defaultEQM2080_R01DetailPivotDto, EQM2080_R01DetailPivotDto } from "./Dto_EQM2080_R01";
import { useEQM2080_R01Store } from "./Store_EQM2080_R01";
import { getEditableDayColIds } from "src/utils/date/dateUtils";
import {
	applyPivotDayCellUpdate,
	EQM2080_DEFAULT_PINNED_COL_IDS,
	buildInspectNmMap,
	buildMonthDayGroupColumnDefs,
	getInspectNmCellClass,
	getInspectNmRowSpan,
	getMonthDayColumns_EQM2080_R01,
	headerNames_EQM2080_R01Detail,
} from "./Util_EQM2080_R01";
import { createCellCssClassRules, GridCellConfig, useCellChangeHandler } from "src/utils/grid/GridHandlerContent";

const COLUMN_STATE_KEY = "EQM2080_R01-cn-detail-grid-column-state-v3";

interface Props {
	gridRef: React.RefObject<AgGridReact<EQM2080_R01DetailPivotDto> | null>;
}

export const CNTable_EQM2080_R01 = (props: Props) => {
	const { gridRef } = props;
	const {
		EQM2080_R01Filter,
		EQM2080_R01CNDetailList,
		setEQM2080_R01CNDetailList,
		originalEQM2080_R01CNDetailList,
		updateEQM2080_R01CNDetail,
		selectedEQM2080_R01CNDetailList,
		deletedEQM2080_R01CNDetailList,
		cnDetailColumnState,
		setCnDetailColumnState,
		cnDetailScrollTop,
		setCnDetailScrollTop,
	} = useEQM2080_R01Store();

	const gridWrapperRef = useRef<HTMLDivElement>(null);

	const {
		getRowId,
		paddedGridData,
		createCellClassRules,
	} = useGridSettingContent({
		gridRef,
		key: "valueIndex",
		gridData: EQM2080_R01CNDetailList,
		defaultGridData: defaultEQM2080_R01DetailPivotDto,
		selectedRow: undefined,
		originalGridData: originalEQM2080_R01CNDetailList,
		updateRow: (index, data) => updateEQM2080_R01CNDetail(index, data),
	});

	const monthDayColumns = useMemo(
		() => getMonthDayColumns_EQM2080_R01(EQM2080_R01Filter.inspectDt)
	,[EQM2080_R01Filter.inspectDt]);

	const inspectNmRowSpanMap = useMemo(
		() => buildInspectNmMap(EQM2080_R01CNDetailList)
	,[EQM2080_R01CNDetailList]);

	const defaultEditableColumns = useMemo(
		() => getEditableDayColIds(monthDayColumns)
	,[monthDayColumns]);

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: [],
		insertedDisabledCells: [],
		commonDisabledCells: [],
		pointerCells: defaultEditableColumns,
		insertedPointerCells: [],
		commonPointerCells: [],
		alignLeftCells: ["inspectNm", "inspectItemNm", "standardValue"],
	}), [defaultEditableColumns]);

	const {
		disabledInsertedColumns,
		disabledColumns,
		pointerInsertedColumns,
		pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<EQM2080_R01DetailPivotDto>({
			idKey: "valueIndex",
			selectedList: selectedEQM2080_R01CNDetailList,
			deletedList: deletedEQM2080_R01CNDetailList,
			disabledInsertedColumns,
			disabledColumns,
			pointerInsertedColumns,
			pointerColumns,
			alignLeftColumns,
		});
		return { ...defaultColDef, cellClassRules: { ...baseRules } };
	}, [createCellClassRules, selectedEQM2080_R01CNDetailList, deletedEQM2080_R01CNDetailList, disabledInsertedColumns, disabledColumns, pointerInsertedColumns, pointerColumns, alignLeftColumns]);

	const handleCellChange = useCellChangeHandler({
		dataList: EQM2080_R01CNDetailList,
		originalDataList: originalEQM2080_R01CNDetailList,
		updateRow: updateEQM2080_R01CNDetail,
		customBeforeUpdate: (updatedData, field, newValue) => {
			const originalRow = originalEQM2080_R01CNDetailList.find(
				(row) => row.valueIndex === updatedData.valueIndex,
			);
			const nextData = applyPivotDayCellUpdate(updatedData, field, newValue, originalRow);
			return nextData ?? updatedData;
		},
	});

	const cellEventHandlers = useDetailTableEvents({
		detailList: EQM2080_R01CNDetailList,
		editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		},
	});

	const baseColumnDefs: ColDef<EQM2080_R01DetailPivotDto>[] = useMemo(() => {
		const dayColumnDefs = buildMonthDayGroupColumnDefs(EQM2080_R01Filter.inspectDt);

		return [
			...getDefaultRowIndexColumnConfig(),
			{
				headerName: "공정명", colId: "inspectNm", field: "inspectNm", width: 120, minWidth: 100,
				editable: false,
				suppressMovable: true,
				rowSpan: (params) => {
					if (params.node?.rowPinned) return 1;
					if (!params.data?.inspectCd) return 1;
					return getInspectNmRowSpan(inspectNmRowSpanMap, params.data.valueIndex);
				},
				cellClass: (params) => getInspectNmCellClass(params, inspectNmRowSpanMap),
			},
			{
				headerName: "점검항목", colId: "inspectItemNm", field: "inspectItemNm", width: 200, minWidth: 120,
				editable: false,
				suppressMovable: true,
			},
			{
				headerName: "기준치", colId: "standardValue", field: "standardValue", width: 100, minWidth: 80,
				editable: false,
				suppressMovable: true,
			},
			...dayColumnDefs,
		];
	}, [EQM2080_R01Filter.inspectDt, inspectNmRowSpanMap]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, EQM2080_DEFAULT_PINNED_COL_IDS)
	,[baseColumnDefs]);

	const columnDefs = useMemo(() => {
		if (cnDetailColumnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<EQM2080_R01DetailPivotDto>({
			baseColumnDefs,
			columnState: cnDetailColumnState,
			headerNames: headerNames_EQM2080_R01Detail,
			fallbackHeaderClass: aggridHeaderClass,
			mergePinnedFromState: false,
		});
	}, [baseColumnDefs, cnDetailColumnState]);

	const pinnedBottomRowData = useMemo(
		() => [{ inspectNm: `${EQM2080_R01CNDetailList.length.toLocaleString()}건` }]
	,[EQM2080_R01CNDetailList]);

	return (
		<div className="w-full h-full flex flex-col gap-[4px]">
			<GridContextWrapper<EQM2080_R01DetailPivotDto>
				gridWrapperRef={gridWrapperRef}
				gridRef={gridRef}
				paddedGridData={paddedGridData}
				setPaddedGridData={setEQM2080_R01CNDetailList}
				defaultRowData={defaultEQM2080_R01DetailPivotDto}
				baseColumnDefs={baseColumnDefs}
				defaultColumnState={defaultColumnState}
				setColumnState={setCnDetailColumnState}
				localStorageKey={COLUMN_STATE_KEY}
				menuItems={undefined}
			>
				<Grid_Excel<EQM2080_R01DetailPivotDto>
					ref={gridRef}
					rowData={paddedGridData}
					getRowId={getRowId}
					columnDefs={columnDefs}
					defaultColDef={cellCssDefaultColDef}
					columnStateKey={COLUMN_STATE_KEY}
					setColumnState={setCnDetailColumnState}
					getDefaultColumnState={() => defaultColumnState}
					scrollTop={cnDetailScrollTop}
					setScrollTop={setCnDetailScrollTop}
					onCellValueChanged={handleCellChange}
					onCellClicked={cellEventHandlers.handleCellClick}
					onCellKeyDown={cellEventHandlers.handleCellKeyDown}
					pinnedBottomRowData={pinnedBottomRowData}
					suppressRowTransform={true}
					animateRows={false}
				/>
			</GridContextWrapper>
		</div>
	);
};
